import React, { useMemo, useState } from "react";
import ReactDOM from "react-dom";

import { useDateTimeFormat } from "../../../hooks/useDateTimeFormat";
import { getMeasurementModeDefinition } from "../../../utils/patientProfileData";
import {
  buildAdherenceScheduleLabel,
  formatAdherenceProgress,
  getQuestionnaireLabel,
  getRecordingModeLabel,
} from "../../../utils/patients/patientListUtils";

function parseTimestamp(value) {
  const ms = Date.parse(value || "");
  return Number.isFinite(ms) ? ms : Number.NEGATIVE_INFINITY;
}

function getPreviousMonth(date) {
  const month = date.getMonth();

  return {
    month: month === 0 ? 11 : month - 1,
    year: month === 0 ? date.getFullYear() - 1 : date.getFullYear(),
  };
}

function getRelativeMonthBucket(value, now = new Date()) {
  const date = new Date(value || "");

  if (!Number.isFinite(date.getTime())) {
    return null;
  }

  if (
    date.getMonth() === now.getMonth() &&
    date.getFullYear() === now.getFullYear()
  ) {
    return "current";
  }

  const previous = getPreviousMonth(now);

  if (
    date.getMonth() === previous.month &&
    date.getFullYear() === previous.year
  ) {
    return "previous";
  }

  return null;
}

function getStatusConfig(actualCount, expectedCount, t) {
  if (expectedCount > 0 && actualCount >= expectedCount) {
    return {
      label: t("profilePage.protocolAdherenceComplete", "Complete"),
      className: "bg-green-100 text-green-800",
      icon: "check_circle",
    };
  }

  if (actualCount > 0) {
    return {
      label: t("profilePage.protocolAdherencePartial", "Partial"),
      className: "bg-orange-100 text-orange-800",
      icon: "pending_actions",
    };
  }

  return {
    label: t("profilePage.protocolAdherenceMissingStatus", "Missing"),
    className: "bg-red-100 text-red-800",
    icon: "error",
  };
}

function getMeasurementModeValue(entry) {
  return getMeasurementModeDefinition(entry)?.modeValue || null;
}

function getLatestEntry(entries) {
  return [...entries].sort(
    (left, right) =>
      parseTimestamp(right?.completedAt || right?.takenAt) -
      parseTimestamp(left?.completedAt || left?.takenAt),
  )[0];
}

function flattenQuestionnaireEntries(questionnaires) {
  return Object.entries(questionnaires || {}).flatMap(
    ([questionnaireType, group]) => {
      const entries = Array.isArray(group?.entries) ? group.entries : [];
      return entries.map((entry) => ({
        ...entry,
        questionnaireType,
      }));
    },
  );
}

function buildRecordingRows(summary, entries, periodKey, now, t) {
  const modes = Array.isArray(summary?.modes) ? summary.modes : [];

  return modes.map((modeSummary) => {
    const periodEntries = entries.filter(
      (entry) =>
        getRelativeMonthBucket(entry?.takenAt, now) === periodKey &&
        getMeasurementModeValue(entry) === modeSummary.mode,
    );
    const latestEntry = getLatestEntry(periodEntries);
    const actualCount = Number(modeSummary.recordingsCount) || 0;
    const expectedCount = Number(modeSummary.expectedCount) || 0;

    return {
      key: `recording:${modeSummary.mode}:${modeSummary.clinicStudyId || "study"}`,
      kind: "recording",
      label: getRecordingModeLabel(modeSummary.mode, t),
      clinicStudyId: modeSummary.clinicStudyId,
      frequency: modeSummary.frequency,
      occurrencesPerPeriod: modeSummary.occurrencesPerPeriod,
      expectedCount,
      actualCount,
      missingCount: Number(modeSummary.missingCount) || 0,
      latestAt: latestEntry?.completedAt || latestEntry?.takenAt || null,
      processingStatus: latestEntry?.processingStatus || null,
      score: null,
      status: getStatusConfig(actualCount, expectedCount, t),
    };
  });
}

function buildQuestionnaireRows(
  summary,
  entries,
  periodKey,
  now,
  showQuestionnaireScores,
  t,
) {
  const assignments = Array.isArray(summary?.assignments)
    ? summary.assignments
    : [];

  return assignments.map((assignment) => {
    const questionnaireId = assignment.questionnaireId;
    const periodEntries = entries.filter(
      (entry) =>
        getRelativeMonthBucket(entry?.takenAt, now) === periodKey &&
        entry?.questionnaireType === questionnaireId,
    );
    const latestEntry = getLatestEntry(periodEntries);
    const actualCount = Number(assignment.responsesCount) || 0;
    const expectedCount = Number(assignment.expectedCount) || 0;

    return {
      key: `questionnaire:${questionnaireId}:${assignment.clinicStudyId || "study"}`,
      kind: "questionnaire",
      label: getQuestionnaireLabel(questionnaireId, t),
      clinicStudyId: assignment.clinicStudyId,
      frequency: assignment.frequency,
      occurrencesPerPeriod: assignment.occurrencesPerPeriod,
      expectedCount,
      actualCount,
      missingCount: Number(assignment.missingCount) || 0,
      latestAt: latestEntry?.takenAt || null,
      processingStatus: null,
      score:
        showQuestionnaireScores &&
        typeof latestEntry?.score === "number" &&
        Number.isFinite(latestEntry.score)
          ? latestEntry.score
          : null,
      status: getStatusConfig(actualCount, expectedCount, t),
    };
  });
}

function buildPeriodRows({
  summary,
  measurementEntries,
  questionnaireEntries,
  periodKey,
  now,
  showQuestionnaireScores,
  t,
}) {
  return {
    recordings: buildRecordingRows(
      summary?.recordings,
      measurementEntries,
      periodKey,
      now,
      t,
    ),
    questionnaires: buildQuestionnaireRows(
      summary?.questionnaires,
      questionnaireEntries,
      periodKey,
      now,
      showQuestionnaireScores,
      t,
    ),
  };
}

function PeriodSummary({ summary, t }) {
  const expected = Number(summary?.expectedItemsCount) || 0;
  const completed = Number(summary?.completedItemsCount) || 0;
  const missing = Number(summary?.missingItemsCount) || 0;
  const ratio = expected > 0 ? Math.round((completed / expected) * 100) : 0;

  return (
    <div className="grid grid-cols-3 gap-2">
      <div className="rounded-lg border border-greyscale-1 px-3 py-2">
        <p className="text-xs font-semibold uppercase text-greyscale-3">
          {t("profilePage.protocolAdherenceExpectedActual", "Expected / actual")}
        </p>
        <p className="mt-1 text-xl font-semibold text-typography">
          {completed}/{expected}
        </p>
      </div>
      <div className="rounded-lg border border-greyscale-1 px-3 py-2">
        <p className="text-xs font-semibold uppercase text-greyscale-3">
          {t("profilePage.protocolAdherenceMissing", "Overdue / missing")}
        </p>
        <p className="mt-1 text-xl font-semibold text-typography">{missing}</p>
      </div>
      <div className="rounded-lg border border-greyscale-1 px-3 py-2">
        <p className="text-xs font-semibold uppercase text-greyscale-3">
          {t("profilePage.protocolAdherenceCompletion", "Completion")}
        </p>
        <p className="mt-1 text-xl font-semibold text-typography">{ratio}%</p>
      </div>
    </div>
  );
}

function AdherenceRow({ row, formatDateTime, t }) {
  const completedAt = formatDateTime(row.latestAt);
  const scheduleLabel = buildAdherenceScheduleLabel(
    row.frequency,
    row.occurrencesPerPeriod,
    t,
  );

  return (
    <div className="rounded-lg border border-greyscale-1 bg-container px-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-typography">{row.label}</p>
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1 text-xs text-greyscale-3">
            <span>
              {t("patientsPage.adherencePanelFrequencyLabel", {
                defaultValue: "Frequency",
              })}
              : {scheduleLabel}
            </span>
            {row.clinicStudyId ? (
              <span>
                {t("patientsPage.adherencePanelStudyLabel", {
                  defaultValue: "Study",
                })}
                : {row.clinicStudyId}
              </span>
            ) : null}
          </div>
        </div>

        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${row.status.className}`}
        >
          <span className="material-symbols-outlined text-base leading-none">
            {row.status.icon}
          </span>
          {row.status.label}
        </span>
      </div>

      <div className="mt-3 grid gap-2 text-sm sm:grid-cols-2 xl:grid-cols-4">
        <div>
          <p className="text-xs font-semibold uppercase text-greyscale-3">
            {t("profilePage.protocolAdherenceExpectedActual", "Expected / actual")}
          </p>
          <p className="text-typography">
            {formatAdherenceProgress(row.actualCount, row.expectedCount, t)}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-greyscale-3">
            {t("profilePage.protocolAdherenceMissing", "Overdue / missing")}
          </p>
          <p className="text-typography">{row.missingCount}</p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-greyscale-3">
            {t(
              "profilePage.protocolAdherenceLatestCompletion",
              "Latest completion",
            )}
          </p>
          <p className="text-typography">
            {[completedAt.date, completedAt.time].filter(Boolean).join(" ") ||
              "--"}
          </p>
        </div>
        <div>
          <p className="text-xs font-semibold uppercase text-greyscale-3">
            {row.kind === "recording"
              ? t(
                  "profilePage.protocolAdherenceProcessingStatus",
                  "Processing status",
                )
              : t("profilePage.scoreHeader", "Score")}
          </p>
          <p className="text-typography">
            {row.kind === "recording"
              ? row.processingStatus || "--"
              : row.score ?? "--"}
          </p>
        </div>
      </div>
    </div>
  );
}

function AdherenceSection({ title, rows, emptyLabel, formatDateTime, t }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-sm font-semibold uppercase text-greyscale-3">
        {title}
      </h3>
      {rows.length ? (
        <div className="flex flex-col gap-2">
          {rows.map((row) => (
            <AdherenceRow
              key={row.key}
              row={row}
              formatDateTime={formatDateTime}
              t={t}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-lg border border-greyscale-1 bg-container px-3 py-6 text-center text-sm text-greyscale-3">
          {emptyLabel}
        </p>
      )}
    </section>
  );
}

export default function PatientAdherenceOverlay({
  currentMonthAdherence,
  previousMonthAdherence,
  measurementEntries = [],
  questionnaires = {},
  showQuestionnaireScores = true,
  open,
  onClose,
  subjectLabel,
  clinicStudySummary,
  t,
  now = new Date(),
}) {
  const { formatDateTime } = useDateTimeFormat();
  const [selectedPeriod, setSelectedPeriod] = useState("current");
  const questionnaireEntries = useMemo(
    () => flattenQuestionnaireEntries(questionnaires),
    [questionnaires],
  );
  const periods = useMemo(
    () => [
      {
        key: "current",
        label: t("profilePage.protocolAdherenceCurrentPeriod", "Current period"),
        summary: currentMonthAdherence,
      },
      {
        key: "previous",
        label: t("profilePage.protocolAdherencePreviousPeriod", "Previous period"),
        summary: previousMonthAdherence,
      },
    ],
    [currentMonthAdherence, previousMonthAdherence, t],
  );
  const activePeriod =
    periods.find((period) => period.key === selectedPeriod) || periods[0];
  const rows = useMemo(
    () =>
      buildPeriodRows({
        summary: activePeriod.summary,
        measurementEntries,
        questionnaireEntries,
        periodKey: activePeriod.key,
        now,
        showQuestionnaireScores,
        t,
      }),
    [
      activePeriod,
      measurementEntries,
      now,
      questionnaireEntries,
      showQuestionnaireScores,
      t,
    ],
  );
  const hasConfiguredItems =
    Number(activePeriod.summary?.expectedItemsCount) > 0 ||
    rows.recordings.length > 0 ||
    rows.questionnaires.length > 0;

  if (!open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
      <button
        type="button"
        aria-label={t(
          "profilePage.closeProtocolAdherence",
          "Close protocol adherence",
        )}
        className="absolute inset-0 cursor-default bg-black/45 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative z-10 flex max-h-[92vh] w-[96vw] max-w-5xl flex-col overflow-hidden rounded-2xl border border-greyscale-1 bg-background shadow-2xl">
        <div className="flex flex-wrap items-start justify-between gap-4 border-b border-greyscale-1 p-4">
          <div className="min-w-0">
            <h2 className="text-2xl font-semibold text-typography">
              {t("profilePage.protocolAdherenceTitle", "Protocol adherence")}
            </h2>
            <p className="mt-1 text-sm text-greyscale-3">
              {t(
                "profilePage.protocolAdherenceSubtitle",
                "Expected vs actual recordings and questionnaires for this subject.",
              )}
            </p>
            <p className="mt-2 text-sm text-greyscale-3">
              {subjectLabel ? `${subjectLabel}` : "--"}
              {clinicStudySummary ? ` · ${clinicStudySummary}` : ""}
            </p>
          </div>

          <button
            type="button"
            className="outlined-button !min-h-0 !px-4 !py-2 !text-sm"
            onClick={onClose}
          >
            {t("profilePage.closeOverlayButton", "Close")}
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="mb-4 inline-flex rounded-lg border border-greyscale-1 bg-container p-1">
            {periods.map((period) => (
              <button
                key={period.key}
                type="button"
                className={[
                  "rounded-md px-3 py-1.5 text-sm font-semibold transition-colors",
                  selectedPeriod === period.key
                    ? "bg-primary text-greyscale-1"
                    : "text-primary hover:bg-greyscale-1",
                ]
                  .filter(Boolean)
                  .join(" ")}
                onClick={() => setSelectedPeriod(period.key)}
              >
                {period.label}
              </button>
            ))}
          </div>

          {hasConfiguredItems ? (
            <div className="flex flex-col gap-5">
              <PeriodSummary summary={activePeriod.summary} t={t} />
              <AdherenceSection
                title={t(
                  "patientsPage.adherencePanelRecordingsSectionTitle",
                  {
                    defaultValue: "Recordings",
                  },
                )}
                rows={rows.recordings}
                emptyLabel={t(
                  "profilePage.protocolAdherenceNoRecordingItems",
                  "No recording adherence items are configured for this period.",
                )}
                formatDateTime={formatDateTime}
                t={t}
              />
              <AdherenceSection
                title={t(
                  "patientsPage.adherencePanelQuestionnairesSectionTitle",
                  {
                    defaultValue: "Questionnaires",
                  },
                )}
                rows={rows.questionnaires}
                emptyLabel={t(
                  "profilePage.protocolAdherenceNoQuestionnaireItems",
                  "No questionnaire adherence items are configured for this period.",
                )}
                formatDateTime={formatDateTime}
                t={t}
              />
            </div>
          ) : (
            <p className="rounded-lg border border-greyscale-1 bg-container px-3 py-10 text-center text-sm text-greyscale-3">
              {t("patientsPage.adherencePanelNoConfiguredItems", {
                defaultValue:
                  "No study adherence items are configured for this subject.",
              })}
            </p>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
